/**
 * Context for sharing admin drawer state between AdminLayout and TopNavigation.
 */

import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react'

interface AdminDrawerContextValue {
    drawerOpen: boolean
    toggleDrawer: () => void
    setDrawerOpen: (open: boolean) => void
}

const AdminDrawerContext = createContext<AdminDrawerContextValue | null>(null)

interface AdminDrawerProviderProps {
    children: ReactNode
    initialOpen?: boolean
}

export const AdminDrawerProvider: React.FC<AdminDrawerProviderProps> = ({
    children,
    initialOpen = true,
}) => {
    const [drawerOpen, setDrawerOpenState] = useState(initialOpen)

    const toggleDrawer = useCallback(() => {
        setDrawerOpenState((prev) => !prev)
    }, [])

    const setDrawerOpen = useCallback((open: boolean) => {
        setDrawerOpenState(open)
    }, [])

    return (
        <AdminDrawerContext.Provider value={{ drawerOpen, toggleDrawer, setDrawerOpen }}>
            {children}
        </AdminDrawerContext.Provider>
    )
}

// Justified exception: Hook export (not a component) - React Refresh limitation
// eslint-disable-next-line react-refresh/only-export-components -- Justified: hook export
export const useAdminDrawer = (): AdminDrawerContextValue => {
    const context = useContext(AdminDrawerContext)
    if (!context) {
        // Return a no-op implementation when not in admin layout
        return {
            drawerOpen: false,
            toggleDrawer: () => {},
            setDrawerOpen: () => {},
        }
    }
    return context
}
